import {
  ConsoleUnitTest,
  getConsoleUnitTestReference,
} from '../../models/unitTests/console';
import {
  type ConsoleTranscriptEntry,
  type ConsoleTranscriptInputEntry,
} from '@cip/code-runner';
import {cloneDeep as deepCopy} from 'lodash-es';
import {useState, useEffect} from 'react';
import {FaTrash, FaPlus, FaEraser, FaQuestionCircle, FaTimes} from 'react-icons/fa';
import {setDoc} from 'firebase/firestore';
import {type LoadedConsoleUnitTestData} from './types';
import {WithEditorData} from '../UnitTestSchema';
import {useDebounce} from 'use-debounce';
import {Button} from 'react-bootstrap';
import {uuidv4} from 'lib0/random';
import {useDocumentDataOnce} from 'react-firebase-hooks/firestore';
import Swal from 'sweetalert2';
import {ASSIGNMENT_DATA_DEBOUNCE_MS} from '../constants';
import type {AssignmentMilestone} from '@cip/core/assignments';
import {useTranslateFunction} from 'contexts/translation/TranslationContext';

export const ConsoleEditor = ({
  loadedData,
  unitTestLoaderParameters,
  isEditable,
}: WithEditorData<LoadedConsoleUnitTestData>) => {
  const t = useTranslateFunction('assignmentEditor');
  const {unitTestData: serverUnitTestData} = loadedData ?? {
    unitTestData: {unitTests: []},
  };
  const {courseId, assignmentId} = unitTestLoaderParameters;
  // Read from the saved metadata, so the dropdown only ever offers milestones
  // that have actually been written by the Milestones section.
  const assignmentMilestones =
    unitTestLoaderParameters.serverAssignmentData?.metaData?.milestones ?? [];
  const [unitTests, setUnitTests] = useState<ConsoleUnitTest[]>(
    serverUnitTestData?.unitTests ?? [],
  );
  const [previouslyUploadedUnitTests, setPreviouslyUploadedUnitTests] =
    useState(unitTests);
  const [debouncedUnitTests] = useDebounce(
    unitTests,
    ASSIGNMENT_DATA_DEBOUNCE_MS,
  );
  const [shouldIgnoreCasingInAutograder, setShouldIgnoreCasingInAutograder] =
    useState(
      serverUnitTestData?.options?.shouldIgnoreCasingInAutograder ?? false,
    );
  const [
    previouslyUploadedShouldIgnoreCasingInAutograder,
    setPreviouslyUploadedShouldIgnoreCasingInAutograder,
  ] = useState(shouldIgnoreCasingInAutograder);
  const [debouncedShouldIgnoreCasingInAutograder] = useDebounce(
    shouldIgnoreCasingInAutograder,
    ASSIGNMENT_DATA_DEBOUNCE_MS,
  );
  const [
    shouldIgnoreWhitespaceInAutograder,
    setShouldIgnoreWhitespaceInAutograder,
  ] = useState(
    serverUnitTestData?.options?.shouldIgnoreWhitespaceInAutograder ?? false,
  );
  const [
    previouslyUploadedShouldIgnoreWhitespaceInAutograder,
    setPreviouslyUploadedShouldIgnoreWhitespaceInAutograder,
  ] = useState(shouldIgnoreWhitespaceInAutograder);
  const [debouncedShouldIgnoreWhitespaceInAutograder] = useDebounce(
    shouldIgnoreWhitespaceInAutograder,
    ASSIGNMENT_DATA_DEBOUNCE_MS,
  );
  const firebaseUnitTestDocumentReference = getConsoleUnitTestReference(
    courseId,
    assignmentId,
  );
  const [unitTestsDocument, unitTestsDocumentLoading, unitTestsDocumentError] =
    useDocumentDataOnce(firebaseUnitTestDocumentReference);
  function hasUnsavedChanged() {
    if (unitTestsDocumentLoading || unitTestsDocumentError) {
      return false;
    }
    if (previouslyUploadedUnitTests.length !== debouncedUnitTests.length) {
      return true;
    }
    return (
      !debouncedUnitTests.every((unitTest, unitTestIdx) => {
        const previousUnitTest = previouslyUploadedUnitTests[unitTestIdx];
        return (
          unitTest.key === previousUnitTest.key &&
          unitTest.name === previousUnitTest.name &&
          unitTest.description === previousUnitTest.description &&
          unitTest.randomSeed === previousUnitTest.randomSeed &&
          unitTest.milestoneId === previousUnitTest.milestoneId &&
          unitTest.transcript.length === previousUnitTest.transcript.length &&
          unitTest.transcript.every((entry, entryIdx) => {
            const previous = previousUnitTest.transcript[entryIdx];
            if (entry.type !== previous.type) return false;
            if (entry.output !== previous.output) return false;
            if (entry.type === 'input' && previous.type === 'input') {
              return entry.input === previous.input;
            }
            return true;
          })
        );
      }) ||
      debouncedShouldIgnoreCasingInAutograder !==
        previouslyUploadedShouldIgnoreCasingInAutograder ||
      debouncedShouldIgnoreWhitespaceInAutograder !==
        previouslyUploadedShouldIgnoreWhitespaceInAutograder
    );
  }

  useEffect(() => {
    if (
      unitTestsDocumentLoading ||
      unitTestsDocumentError ||
      !unitTestsDocument
    ) {
      return;
    }
    // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    setUnitTests(unitTestsDocument.unitTests ?? []);
    // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    setPreviouslyUploadedUnitTests(unitTestsDocument.unitTests ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  }, [unitTestsDocumentLoading]);

  const shouldSave = hasUnsavedChanged();
  useEffect(() => {
    if (!shouldSave) {
      return;
    }
    setDoc(firebaseUnitTestDocumentReference, {
      unitTests: debouncedUnitTests,
      options: {
        shouldIgnoreCasingInAutograder: debouncedShouldIgnoreCasingInAutograder,
        shouldIgnoreWhitespaceInAutograder:
          debouncedShouldIgnoreWhitespaceInAutograder,
      },
    })
      .then(() => {
        setPreviouslyUploadedUnitTests(debouncedUnitTests);
        setPreviouslyUploadedShouldIgnoreCasingInAutograder(
          debouncedShouldIgnoreCasingInAutograder,
        );
        setPreviouslyUploadedShouldIgnoreWhitespaceInAutograder(
          debouncedShouldIgnoreWhitespaceInAutograder,
        );
      })
      .catch(error => {
        Swal.fire({
          title: 'Error saving unit tests',
          icon: 'error',
          toast: true,
          position: 'bottom',
          showConfirmButton: false,
        });
        console.error('Error saving unit tests: ', error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  }, [shouldSave]);

  const deleteUnitTest = i => {
    setUnitTests(oldUnitTests => {
      const newUnitTests = deepCopy(oldUnitTests);
      newUnitTests.splice(i, 1);
      return newUnitTests;
    });
  };

  const addUnitTest = () => {
    setUnitTests(oldUnitTests => {
      // since newUnitTests is a compound object
      // we use this way of updating state (otherwise view
      // wont react)
      const newUnitTests = deepCopy(oldUnitTests);
      newUnitTests.push({
        description: '',
        name: `New Console Test`,
        key: uuidv4(),
        transcript: [],
      });
      return newUnitTests;
    });
  };

  const setTestName = (i: number, name: string) => {
    setUnitTests(oldUnitTests => {
      const newUnitTests = deepCopy(oldUnitTests);
      newUnitTests[i].name = name;
      return newUnitTests;
    });
  };

  function setTestDescription(i: number, description: string) {
    setUnitTests(oldUnitTests => {
      const newUnitTests = deepCopy(oldUnitTests);
      newUnitTests[i].description = description;
      return newUnitTests;
    });
  }

  function setTranscript(
    i: number,
    transcript: ConsoleTranscriptEntry[],
  ) {
    setUnitTests(oldUnitTests => {
      const newUnitTests = deepCopy(oldUnitTests);
      newUnitTests[i].transcript = transcript;
      return newUnitTests;
    });
  }

  function setRandomSeed(i: number, randomSeed: number | undefined) {
    setUnitTests(oldUnitTests => {
      const newUnitTests = deepCopy(oldUnitTests);
      newUnitTests[i].randomSeed = randomSeed;
      return newUnitTests;
    });
  }

  function setMilestoneId(i: number, milestoneId: string | undefined) {
    setUnitTests(oldUnitTests => {
      const newUnitTests = deepCopy(oldUnitTests);
      newUnitTests[i].milestoneId = milestoneId;
      return newUnitTests;
    });
  }

  return (
    <div>
      <div className="d-flex flex-column mt-3">
        <div className="d-flex flex-row">
          <div className="d-flex flex-row gap-4 mb-3">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="ignoreCasing"
                checked={shouldIgnoreCasingInAutograder}
                disabled={!isEditable}
                onChange={e =>
                  setShouldIgnoreCasingInAutograder(e.target.checked)
                }
              />
              <label className="form-check-label" htmlFor="ignoreCasing">
                {t('Ignore Casing')}
                <span
                  className="ms-1"
                  title={
                    t(
                      'When enabled, the autograder will ignore differences in letter casing (e.g., "Hello" and "hello" will be considered equal)',
                    ) as string
                  }
                  style={{cursor: 'help'}}
                >
                  <FaQuestionCircle />
                </span>
              </label>
            </div>
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="ignoreWhitespace"
                checked={shouldIgnoreWhitespaceInAutograder}
                disabled={!isEditable}
                onChange={e =>
                  setShouldIgnoreWhitespaceInAutograder(e.target.checked)
                }
              />
              <label className="form-check-label" htmlFor="ignoreWhitespace">
                {t('Ignore Whitespace')}
                <span
                  className="ms-1"
                  title={
                    t(
                      'When enabled, the autograder will ignore differences in whitespace (spaces, tabs, newlines) when comparing output',
                    ) as string
                  }
                  style={{cursor: 'help'}}
                >
                  <FaQuestionCircle />
                </span>
              </label>
            </div>
          </div>
        </div>
        {unitTests.map(function (unitTestData, i) {
          return (
            <EditableConsoleUnitTest
              unitTestData={unitTestData}
              deleteUnitTest={() => deleteUnitTest(i)}
              setTestName={name => setTestName(i, name)}
              setTestDescription={description =>
                setTestDescription(i, description)
              }
              setTranscript={transcript => setTranscript(i, transcript)}
              setRandomSeed={randomSeed => setRandomSeed(i, randomSeed)}
              milestones={assignmentMilestones}
              setMilestoneId={milestoneId => setMilestoneId(i, milestoneId)}
              key={unitTests[i].key ?? uuidv4()}
              isEditable={isEditable}
            />
          );
        })}
      </div>
      <button
        onClick={() => addUnitTest()}
        className="btn btn-secondary "
        disabled={!isEditable}
      >
        {t('Add Unit Test')}
      </button>
    </div>
  );
};

interface EditableConsoleUnitTestProps {
  unitTestData: ConsoleUnitTest;
  setTestName: (name: string) => void;
  setTestDescription: (description: string) => void;
  setTranscript: (transcript: ConsoleTranscriptEntry[]) => void;
  setRandomSeed: (randomSeed: number | undefined) => void;
  /**
   * The assignment's milestone ladder. Empty for an assignment graded all at
   * once, in which case no milestone control is shown at all.
   */
  milestones: AssignmentMilestone[];
  /**
   * Assigns this case to a milestone, or to the whole assignment when passed
   * `undefined`.
   */
  setMilestoneId: (milestoneId: string | undefined) => void;
  deleteUnitTest: () => void;
  isEditable: boolean;
}

export const EditableConsoleUnitTest = ({
  unitTestData,
  setTranscript,
  setTestName,
  setTestDescription,
  setRandomSeed,
  milestones,
  setMilestoneId,
  deleteUnitTest,
  isEditable,
}: EditableConsoleUnitTestProps) => {
  const {transcript, name, description, randomSeed, milestoneId} =
    unitTestData;
  const [seedExpanded, setSeedExpanded] = useState(randomSeed !== undefined);
  const t = useTranslateFunction('assignmentEditor');
  function addEntry(isPrint: boolean) {
    const newEntry: ConsoleTranscriptEntry = isPrint
      ? {type: 'print', output: ''}
      : {type: 'input', output: '', input: ''};
    const newTranscript = deepCopy(unitTestData.transcript);
    newTranscript.push(newEntry);
    setTranscript(newTranscript);
  }

  function removeEntry(idx: number) {
    const newTranscript = deepCopy(unitTestData.transcript);
    newTranscript.splice(idx, 1);
    setTranscript(newTranscript);
  }

  function updateOutput(idx: number, output: string) {
    if (idx < 0 || idx >= transcript.length) return;
    const newTranscript = deepCopy(transcript);
    newTranscript[idx].output = output;
    setTranscript(newTranscript);
  }

  function updateInput(idx: number, input: string) {
    if (idx < 0 || idx >= transcript.length) return;
    const newTranscript = deepCopy(transcript);
    const target = newTranscript[idx];
    if (target.type !== 'input') return;
    target.input = input;
    setTranscript(newTranscript);
  }

  const renderedEntries = transcript.map((entry, idx) => {
    if (entry.type === 'print') {
      return (
        <PrintUnit
          output={entry.output}
          setOutput={output => updateOutput(idx, output)}
          // eslint-disable-next-line @eslint-react/no-array-index-key -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
          key={idx}
          removeSelf={() => removeEntry(idx)}
          isEditable={isEditable}
        />
      );
    }
    return (
      <InputUnit
        data={entry}
        // eslint-disable-next-line @eslint-react/no-array-index-key -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
        key={idx}
        removeSelf={() => removeEntry(idx)}
        output={entry.output}
        setOutput={output => updateOutput(idx, output)}
        input={entry.input}
        setInput={input => updateInput(idx, input)}
        isEditable={isEditable}
      />
    );
  });

  return (
    <div className="border rounded p-2 mb-3">
      <input
        type={'text'}
        className="form-control mb-2"
        placeholder={t('Test Name') as string}
        value={name}
        onChange={e => {
          setTestName(e.target.value);
        }}
        disabled={!isEditable}
      />
      <input
        type={'text'}
        className="form-control mb-2"
        placeholder="Test Description"
        value={description}
        onChange={e => {
          setTestDescription(e.target.value);
        }}
        disabled={!isEditable}
      />
      {milestones.length > 0 ? (
        <div className="d-flex align-items-center mb-2">
          <strong style={{minWidth: '80px'}}>{t('Milestone')}</strong>
          <select
            className="form-select"
            value={milestoneId ?? ''}
            aria-label={t('Milestone this test belongs to', {
              forceString: true,
            })}
            onChange={e => {
              setMilestoneId(e.target.value === '' ? undefined : e.target.value);
            }}
            disabled={!isEditable}
          >
            <option value="">
              {t('Every milestone (not step-specific)')}
            </option>
            {milestones.map((milestone, index) => (
              <option key={milestone.id} value={milestone.id}>
                {index + 1}. {milestone.title}
              </option>
            ))}
          </select>
        </div>
      ) : null}
      {seedExpanded ? (
        <div className="d-flex mt-3">
          <strong style={{minWidth: '80px'}}>{t('Random Seed')}</strong>
          <input
            type="number"
            step={1}
            className="form-control mb-2"
            placeholder={t('e.g. 42') as string}
            value={randomSeed ?? ''}
            onChange={e => {
              const raw = e.target.value;
              setRandomSeed(raw === '' ? undefined : Number(raw));
            }}
            disabled={!isEditable}
          />
          <Button
            className="mb-2"
            variant="light"
            onClick={() => {
              setRandomSeed(undefined);
              setSeedExpanded(false);
            }}
            disabled={!isEditable}
          >
            <FaTimes />
          </Button>
        </div>
      ) : (
        <div className="d-flex align-items-center mt-3 mb-2 gap-2">
          <Button
            variant="light"
            onClick={() => setSeedExpanded(true)}
            disabled={!isEditable}
          >
            <FaPlus /> {t('Random Seed')}
          </Button>
          <span
            title={
              t(
                "Seeds Python's random module before the student code runs, so tests using random.* are deterministic.",
              ) as string
            }
            style={{cursor: 'help'}}
          >
            <FaQuestionCircle />
          </span>
        </div>
      )}
      {renderedEntries}
      <Button
        className="mb-3"
        variant="light"
        onClick={() => addEntry(true)}
        disabled={!isEditable}
      >
        <FaPlus /> {t('Print Prompt')}
      </Button>
      <Button
        className="mb-3 ml-3"
        variant="light"
        onClick={() => addEntry(false)}
        disabled={!isEditable}
      >
        <FaPlus /> {t('Input Prompt')}
      </Button>
      <Button
        onClick={deleteUnitTest}
        className="mb-3 ml-3"
        variant="light"
        disabled={!isEditable}
      >
        <FaTrash />
      </Button>
    </div>
  );
};

interface PrintUnitProps {
  output: string;
  setOutput: (output: string) => void;
  removeSelf: () => void;
  isEditable: boolean;
}
const PrintUnit = ({
  output,
  setOutput,
  removeSelf,
  isEditable,
}: PrintUnitProps) => {
  const t = useTranslateFunction('assignmentEditor');
  return (
    <div className="d-flex mt-3">
      <strong style={{minWidth: '80px'}}>{t('Print')}</strong>
      <input
        type={'text'}
        className="form-control mb-2"
        placeholder={t('Place expected output here!') as string}
        value={output}
        onChange={e => {
          setOutput(e.target.value);
        }}
        disabled={!isEditable}
      />
      <Button
        className="mb-2"
        variant="light"
        onClick={removeSelf}
        disabled={!isEditable}
      >
        <FaEraser />
      </Button>
    </div>
  );
};

interface InputUnitProps {
  data: ConsoleTranscriptInputEntry;
  removeSelf: () => void;
  output: string;
  setOutput: (output: string) => void;
  input: string;
  setInput: (input: string) => void;
  isEditable: boolean;
}
const InputUnit = ({
  data,
  removeSelf,
  setOutput,
  setInput,
  isEditable,
}: InputUnitProps) => {
  const {output, input} = data;
  const t = useTranslateFunction('assignmentEditor');
  return (
    <div className="d-flex mt-3">
      <strong style={{minWidth: '80px'}}>{t('Input')}</strong>
      <input
        type={'text'}
        className="form-control mb-2"
        placeholder={t('Place Prompt!') as string}
        value={output ?? ''}
        onChange={e => {
          setOutput(e.target.value);
        }}
        disabled={!isEditable}
      />

      <input
        type={'text'}
        className="form-control mb-2"
        placeholder={t('Place input data!') as string}
        value={input ?? ''}
        onChange={e => {
          setInput(e.target.value);
        }}
        disabled={!isEditable}
      />
      <Button
        className="mb-2"
        variant="light"
        onClick={removeSelf}
        disabled={!isEditable}
      >
        <FaEraser />
      </Button>
    </div>
  );
};
